import { Card } from "@rumbledirect/ui";
import { Table } from "@/components/v2";
import { Col, Flex, message, Row } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { TableColumnsType } from "antd";
import {
  deleteAppointmentTimeSlotPlanById,
  fetchAppointmentTimeSlotPlanById,
} from "../../../../lib/api/resources/appointmentTimeSlotPlans";
import { AppointmentTimeSlotPlanDetailCard } from "./components/AppointmentTimeSlotPlanDetailCard";
import AppointmentTimeSlotPlanItemsList from "./components/AppointmentTimeSlotPlanItemsList";

interface SelectedServicePartnerRow {
  key: string;
  groupName: string | null;
  name: string;
  city: string | null;
}

export const AppointmentTimeSlotPlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  if (!id) return null;

  const appointmentTimeSlotPlanQuery = useQuery({
    queryKey: ["appointmentTimeSlotPlan", id],
    queryFn: () => fetchAppointmentTimeSlotPlanById(id),
  });

  const deleteAppointmentTimeSlotPlanMutation = useMutation({
    mutationFn: () => deleteAppointmentTimeSlotPlanById(id),
    onSuccess: () => {
      navigate(`/appointment-time-slots/plans`);
      message.success("Planning succesvol verwijderd");
    },
    onError: () =>
      message.error(
        "Er is een fout opgetreden bij het verwijderen van de planning",
      ),
  });

  const selectedServicePartnersColumns: TableColumnsType<SelectedServicePartnerRow> =
    [
      {
        title: "Groep Naam",
        dataIndex: "groupName",
        key: "groupName",
        render: (groupName: string | null) => groupName || "-",
      },
      {
        title: "Naam",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Plaats",
        dataIndex: "city",
        key: "city",
        render: (city: string | null) => city || "-",
      },
    ];

  const selectedServicePartnersRows: SelectedServicePartnerRow[] =
    appointmentTimeSlotPlanQuery.data?.servicePartners?.map(
      (servicePartner) => ({
        key: servicePartner.id,
        groupName: servicePartner.groupName ?? null,
        name: servicePartner.name || servicePartner.fullName || "-",
        city: servicePartner.city ?? servicePartner.address?.city ?? null,
      }),
    ) ?? [];

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Flex vertical gap={16}>
            <AppointmentTimeSlotPlanDetailCard
              deleteMutation={deleteAppointmentTimeSlotPlanMutation}
              appointmentTimeSlotPlan={appointmentTimeSlotPlanQuery.data}
              isLoading={appointmentTimeSlotPlanQuery.isFetching}
            />
          </Flex>
        </Col>

        {(appointmentTimeSlotPlanQuery.data?.planItems?.length ||
          selectedServicePartnersRows.length) > 0 && (
          <Col span={12}>
            <Flex vertical gap={16}>
              {appointmentTimeSlotPlanQuery.data?.planItems &&
                appointmentTimeSlotPlanQuery.data.planItems.length > 0 && (
                  <Card
                    loading={appointmentTimeSlotPlanQuery.isFetching}
                    title="Tijdsloten"
                  >
                    <AppointmentTimeSlotPlanItemsList
                      items={appointmentTimeSlotPlanQuery.data?.planItems}
                      showActions={false}
                    />
                  </Card>
                )}

              <Card
                loading={appointmentTimeSlotPlanQuery.isFetching}
                title="Geselecteerde herstellers"
              >
                <Table
                  columns={selectedServicePartnersColumns}
                  dataSource={selectedServicePartnersRows}
                  size="small"
                  pagination={false}
                  locale={{ emptyText: "Geen herstellers geselecteerd" }}
                />
              </Card>
            </Flex>
          </Col>
        )}
      </Row>
    </div>
  );
};
